import { useState } from 'react';
import { ChevronDown, ChevronUp, Send } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  Input,
  Select,
  Textarea,
  cn,
  type SelectOption,
} from '../../ui';
import {
  blankDailyUpdateDraft,
  validateDailyUpdateDraft,
} from '../../features/dailyUpdates/dailyUpdateModel';
import type { DailyUpdateDraft } from '../../features/dailyUpdates/types';
import type { Task } from '../../features/tasks/types';
import type { WorkstreamSummary } from '../../features/workstreams/types';
import type { GoalSummary } from '../../features/goals/types';
import { PeopleInput } from '../common/PeopleInput';
import { usePeopleDirectory } from '../../features/teams/usePeopleDirectory';

export interface DailyUpdateComposerProps {
  /** Default engineer alias (the current user). */
  defaultEngineer: string;
  /** Tasks for the story picker. */
  tasks: Task[];
  /** Workstreams for the optional workstream picker. */
  workstreams: WorkstreamSummary[];
  /** Goals for the optional goal picker. */
  goals: GoalSummary[];
  onPost: (draft: DailyUpdateDraft) => Promise<unknown>;
}

/**
 * The extremely-lightweight daily-update composer. The goal is a useful update
 * in ~1 minute: pick a story, jot "today / next", optionally a result and a
 * blocker, and post. Secondary links (engineer override, yesterday, workstream,
 * goal) live behind a "More" toggle so the common path stays two fields + post.
 *
 * AUTO-COLLECT extension point (deferred): a future collector would pre-fill
 * this draft from Jira / repo / deploy activity for the human to verify before
 * posting — the composer + feed are unchanged. Until then, entry is manual.
 */
export function DailyUpdateComposer({
  defaultEngineer,
  tasks,
  workstreams,
  goals,
  onPost,
}: DailyUpdateComposerProps) {
  const [draft, setDraft] = useState<DailyUpdateDraft>(
    blankDailyUpdateDraft(defaultEngineer),
  );
  const { people } = usePeopleDirectory();
  const [showMore, setShowMore] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const storyOptions: SelectOption[] = tasks.map((t) => ({
    value: t.id,
    label: t.title || t.id,
  }));
  const workstreamOptions: SelectOption[] = workstreams.map((w) => ({
    value: w.id,
    label: w.title || w.id,
  }));
  const goalOptions: SelectOption[] = goals.map((g) => ({
    value: g.id,
    label: g.title || g.id,
  }));

  const set = <K extends keyof DailyUpdateDraft>(
    key: K,
    value: DailyUpdateDraft[K],
  ) => setDraft((d) => ({ ...d, [key]: value }));

  const submit = async () => {
    const next: DailyUpdateDraft = {
      ...draft,
      engineer: draft.engineer.trim() || defaultEngineer,
    };
    const found = validateDailyUpdateDraft(next);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    setSaving(true);
    try {
      await onPost(next);
      // Reset for the next update, keeping the engineer + today's date.
      setDraft(blankDailyUpdateDraft(defaultEngineer));
      setShowMore(false);
      setProblems([]);
    } catch (e) {
      setProblems([e instanceof Error ? e.message : 'Failed to post update.']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardBody className="space-y-3">
        {problems.length > 0 && (
          <ul className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {problems.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
          <Select
            label="Story (optional)"
            placeholder="No story — general update"
            value={draft.storyId ?? ''}
            options={storyOptions}
            onChange={(e) => set('storyId', e.target.value || undefined)}
          />
          <Input
            type="date"
            label="Date"
            value={draft.date}
            onChange={(e) => set('date', e.target.value)}
            className="sm:w-40"
          />
        </div>

        <Textarea
          label="Today / next"
          placeholder="What you're on now (or what's next)…"
          rows={2}
          value={draft.today ?? ''}
          onChange={(e) => set('today', e.target.value)}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Textarea
            label="Result / learning (optional)"
            rows={2}
            value={draft.resultLearning ?? ''}
            onChange={(e) => set('resultLearning', e.target.value)}
          />
          <Textarea
            label="Blocker (optional)"
            hint="Note it here — you can convert it to a tracked blocker from the feed."
            rows={2}
            value={draft.blocker ?? ''}
            onChange={(e) => set('blocker', e.target.value)}
          />
        </div>

        <button
          type="button"
          onClick={() => setShowMore((s) => !s)}
          className="inline-flex items-center gap-1 text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-200"
        >
          {showMore ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
          {showMore ? 'Fewer details' : 'More details'}
        </button>

        <div className={cn('space-y-3', !showMore && 'hidden')}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <PeopleInput
              label="Engineer"
              value={draft.engineer}
              people={people}
              onChange={(alias) => set('engineer', alias)}
            />
            <Textarea
              label="Yesterday (optional)"
              rows={2}
              value={draft.yesterday ?? ''}
              onChange={(e) => set('yesterday', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Select
              label="Workstream (optional)"
              placeholder="None"
              value={draft.workstreamId ?? ''}
              options={workstreamOptions}
              onChange={(e) => set('workstreamId', e.target.value || undefined)}
            />
            <Select
              label="Goal (optional)"
              placeholder="None"
              value={draft.goalId ?? ''}
              options={goalOptions}
              onChange={(e) => set('goalId', e.target.value || undefined)}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            loading={saving}
            leftIcon={<Send className="h-4 w-4" />}
            onClick={submit}
          >
            Post update
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default DailyUpdateComposer;
